import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Checkbox from '../../components/ui/Checkbox';
import SearchableSelectBox from '../../components/ui/SearchableSelectBox';
import FastAPIClient from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { fetchInventoryForStore, fetchInventoryForStoreSales, fetchServicesForStoreSale, API_BASE_PATH } from '../../apiHelper';
import { Loader2, Save, X } from 'lucide-react';

const client = new FastAPIClient();

const DiscountRuleForm = ({ user, stores, categories, DiscountTypeEnum, AppliesToEnum, ApprovalThresholdType, initialData, onSaveSuccess, onCancel }) => {
    const { showToast } = useToast();

    const [formState, setFormState] = useState({
        creator_id: user?.users_id || null,
        name: '',
        description: '',
        discount_type: DiscountTypeEnum.PERCENTAGE,
        value: '',
        buy_quantity: '',
        get_quantity: '',
        applies_to: AppliesToEnum.ORDER,
        target_product_id: null,
        target_category_id: null,
        minimum_purchase_amount: '',
        minimum_purchase_quantity: '',
        start_date: '',
        end_date: '',
        is_active: true,
        is_store_wide: false,
        requires_coupon_code: false,
        coupon_code: '',
        max_uses_per_customer: '',
        total_max_uses: '',
        approval_threshold_type: ApprovalThresholdType.NONE,
        approval_threshold_value: '',
        // Additional fields for payload (not directly in schema but needed for API)
        store_id: null, // For store-specific discounts
        tiers: [{ tier_id:'', min_quantity: '', max_quantity: '', tier_value: '' }], // Default first tier
    });

    const [loadingInventory, setLoadingInventory] = useState(false);
    const [inventory, setInventory] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            // console.log("INITIAL DATA:\n",initialData);
            setFormState(prev => ({
                ...prev,
                ...initialData,
                // Format dates for input fields
                start_date: initialData.start_date ? new Date(initialData.start_date).toISOString().slice(0, 16) : '',
                end_date: initialData.end_date ? new Date(initialData.end_date).toISOString().slice(0, 16) : '',
                // Ensure decimal fields are numbers if they come as strings
                value: parseFloat(initialData.value) || '',
                minimum_purchase_amount: parseFloat(initialData.minimum_purchase_amount) || '',
                approval_threshold_value: parseFloat(initialData.approval_threshold_value) || '',
                // Set store_id if it exists in initialData
                store_id: initialData.store_id || null,
                tiers: initialData.tiers?.length > 0
                    ? initialData.tiers
                    : [{ tier_id:'', min_quantity: '', max_quantity: '', tier_value: '' }],
            }));
        } else {
            // Reset form for new rule, ensure creator_id is set
            setFormState(prev => ({
                ...prev,
                creator_id: user?.users_id || null,
                name: '',
                description: '',
                discount_type: DiscountTypeEnum.PERCENTAGE,
                value: '',
                buy_quantity: '',
                get_quantity: '',
                applies_to: AppliesToEnum.ORDER,
                target_product_id: null,
                target_category_id: null,
                minimum_purchase_amount: '',
                minimum_purchase_quantity: '',
                start_date: '',
                end_date: '',
                is_active: true,
                is_store_wide: false,
                requires_coupon_code: false,
                coupon_code: '',
                max_uses_per_customer: '',
                total_max_uses: '',
                approval_threshold_type: ApprovalThresholdType.NONE,
                approval_threshold_value: '',
                store_id: null,
            }));
        }
    }, [initialData, user, DiscountTypeEnum, AppliesToEnum, ApprovalThresholdType]);

    const handleTierChange = (index, field, value) => {
        const newTiers = [...formState.tiers];
        newTiers[index][field] = value;
        setFormState(prev => ({ ...prev, tiers: newTiers }));
    };

    const addTier = () => {
        setFormState(prev => ({
            ...prev,
            tiers: [...prev.tiers, { tier_id:'', min_quantity: '', max_quantity: '', tier_value: '' }]
        }));
    };

    const removeTier = (index) => {
        const newTiers = formState.tiers.filter((_, i) => i !== index);
        setFormState(prev => ({ ...prev, tiers: newTiers }));
    };

    // Fetch inventory when store_id and applies_to is 'product'
    useEffect(() => {
        const loadInventory = async () => {
            if (formState.applies_to === AppliesToEnum.PRODUCT && formState.store_id && user) {
                setLoadingInventory(true);
                try {
                    const payload = {
                        users_id: user.users_id,
                        logon_id: user.logon_id,
                        hpwc: user.hpwc,
                        store_id: formState.store_id,
                        offset: 0, // Fetch all for selection
                        limit: 10000 // Adjust limit as needed
                    };
                    const fetchedServicesItems = await fetchServicesForStoreSale(payload);
                    const fetchedInventoryLots = await fetchInventoryForStoreSales(payload);
                    // console.log(fetchedInventoryLots);
                    const allLots = [...fetchedServicesItems, ...fetchedInventoryLots];
                    // // Aggregate inventory lots by catentry_id to get unique products
                    const aggregatedProductsMap = new Map();
                    allLots.forEach(lot => {
                        // console.log(lot)
                        const catentryId = lot.catentry.catentry_id;
                        if (!aggregatedProductsMap.has(catentryId)) {
                            aggregatedProductsMap.set(catentryId, {
                                id: catentryId,
                                name: lot.catentry.productname,
                                partnumber: lot.catentry.partnumber,
                            });
                        }
                    });
                    setInventory(Array.from(aggregatedProductsMap.values()));
                } catch (err) {
                    showToast(`Failed to load inventory for selected store: ${err.message || err}`, 'error');
                    console.error("Error loading inventory:", err);
                    setInventory([]);
                } finally {
                    setLoadingInventory(false);
                }
            } else {
                setInventory([]); // Clear inventory if not applicable
            }
        };
        loadInventory();
    }, [formState.applies_to, formState.store_id, user, showToast, AppliesToEnum.PRODUCT]);

    const handleChange = useCallback((e) => {
        const { id, value, type, checked } = e.target;
        setFormState(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const handleSelectChange = useCallback((name, value) => {
        setFormState(prev => ({
            ...prev,
            [name]: value,
            // Reset related fields when applies_to or discount_type changes
            ...(name === 'applies_to' && value !== AppliesToEnum.PRODUCT && { target_product_id: null }),
            ...(name === 'applies_to' && value !== AppliesToEnum.CATEGORY && { target_category_id: null }),
            ...(name === 'discount_type' && value !== DiscountTypeEnum.BUY_X_GET_Y && { buy_quantity: '', get_quantity: '' }),
            ...(name === 'requires_coupon_code' && !value && { coupon_code: '' }),
            ...(name === 'approval_threshold_type' && value === ApprovalThresholdType.NONE && { approval_threshold_value: '' }),
        }));
    }, [AppliesToEnum, DiscountTypeEnum, ApprovalThresholdType]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formState,
                // Ensure numeric values are converted
                value: parseFloat(formState.value) || 0,
                minimum_purchase_amount: parseFloat(formState.minimum_purchase_amount) || null,
                minimum_purchase_quantity: parseInt(formState.minimum_purchase_quantity, 10) || null,
                max_uses_per_customer: parseInt(formState.max_uses_per_customer, 10) || null,
                total_max_uses: parseInt(formState.total_max_uses, 10) || null,
                approval_threshold_value: parseFloat(formState.approval_threshold_value) || null,
                // Ensure dates are correctly formatted to ISO strings if needed by backend
                start_date: formState.start_date ? new Date(formState.start_date).toISOString() : null,
                end_date: formState.end_date ? new Date(formState.end_date).toISOString() : null,
                tiers: formState.discount_type === DiscountTypeEnum.TIERED_PRICING
                    ? formState.tiers.map(t => ({
                        tier_id: parseInt(t.tier_id),
                        min_quantity: parseFloat(t.min_quantity),
                        max_quantity: t.max_quantity ? parseFloat(t.max_quantity) : null,
                        tier_value: parseFloat(t.tier_value),
                    })) :
                    null
            };

            // Clean up fields not relevant to the selected discount_type or applies_to
            if (payload.discount_type !== DiscountTypeEnum.BUY_X_GET_Y) {
                delete payload.buy_quantity;
                delete payload.get_quantity;
            }
            if (payload.DiscountTypeEnum === DiscountTypeEnum.TIERED_PRICING) {
                payload.value = 0;
            }
            if (payload.applies_to !== AppliesToEnum.PRODUCT) {
                delete payload.target_product_id;
            }
            if (payload.applies_to !== AppliesToEnum.CATEGORY) {
                delete payload.target_category_id;
            }
            if (!payload.requires_coupon_code) {
                delete payload.coupon_code;
            }
            if (payload.approval_threshold_type === ApprovalThresholdType.NONE) {
                delete payload.approval_threshold_value;
            }

            // Remove empty strings for optional fields that should be null
            for (const key in payload) {
                if (payload[key] === '') {
                    payload[key] = null;
                }
            }

            let response;
            if (initialData && initialData.discountrule_id) {
                console.log(payload)
                response = await client.postData('/accounting/update-discount-rule', payload);
                showToast("Discount rule updated successfully!", 'success');
            } else {
                // console.log(payload)
                response = await client.postData('/accounting/create-discount-rule', payload);
                showToast("Discount rule created successfully!", 'success');
            }
            onSaveSuccess();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to save discount rule: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error("Error saving discount rule:", err);
        } finally {
            setSaving(false);
        }
    };

    const storeOptions = useMemo(() => stores.map(store => ({
        value: store.store_id,
        label: store.identifier
    })), [stores]);

    const categoryOptions = useMemo(() => categories.map(category => ({
        value: category.category_id,
        label: category.identifier
    })), [categories]);

    const productOptions = useMemo(() => inventory.map(product => ({
        value: product.id,
        label: `${product.name} (${product.partnumber})`
    })), [inventory]);

    return (
        <div className="p-6 bg-white rounded-lg shadow-md h-full overflow-y-auto custom-scrollbar">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
                {initialData ? "Edit Discount Rule" : "Create New Discount Rule"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                    label="Rule Name"
                    id="name"
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="e.g., Summer Sale 20% Off"
                    required
                />
                <Input
                    label="Description"
                    id="description"
                    value={formState.description}
                    onChange={handleChange}
                    type="textarea"
                    placeholder="Detailed description of the discount rule."
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Select
                        label="Discount Type"
                        id="discount_type"
                        value={formState.discount_type}
                        onChange={(e) => handleSelectChange('discount_type', e.target.value)}
                        options={Object.values(DiscountTypeEnum).map(type => ({ value: type, label: type.replace(/_/g, ' ').toUpperCase() }))}
                        required
                    />
                    <Input
                        label="Discount Value"
                        id="value"
                        type="number"
                        step="0.01"
                        value={formState.value}
                        onChange={handleChange}
                        placeholder={formState.discount_type === DiscountTypeEnum.PERCENTAGE ? "e.g., 20 (for 20%)" : "e.g., 5.00 (for $5)"}
                        // required
                    />
                </div>
                {formState.discount_type === DiscountTypeEnum.TIERED_PRICING && (
                    <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                        <h3 className="font-semibold text-sm text-gray-700">Volume Tiers</h3>
                        {formState.tiers.map((tier, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                <Input
                                    label="Min Qty"
                                    type="number"
                                    value={tier.min_quantity}
                                    onChange={(e) => handleTierChange(index, 'min_quantity', e.target.value)}
                                    placeholder="1"
                                    required
                                />
                                <Input
                                    label="Max Qty (Optional)"
                                    type="number"
                                    value={tier.max_quantity}
                                    onChange={(e) => handleTierChange(index, 'max_quantity', e.target.value)}
                                    placeholder="∞"
                                />
                                <Input
                                    label="Tier Value (Price/%)"
                                    type="number"
                                    step="0.01"
                                    value={tier.tier_value}
                                    onChange={(e) => handleTierChange(index, 'tier_value', e.target.value)}
                                    placeholder="Price or %"
                                    required
                                />
                                <div className="flex pb-2">
                                    {formState.tiers.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="danger"
                                            className="p-2 h-10 w-10"
                                            onClick={() => removeTier(index)}
                                        >
                                            <X size={16} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={addTier}
                            className="text-sm"
                        >
                            + Add Tier
                        </Button>
                    </div>
                )}

                {formState.discount_type === DiscountTypeEnum.BUY_X_GET_Y && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                            label="Buy Quantity (X)"
                            id="buy_quantity"
                            type="number"
                            step="1"
                            value={formState.buy_quantity}
                            onChange={handleChange}
                            placeholder="e.g., 2"
                            required
                        />
                        <Input
                            label="Get Quantity (Y)"
                            id="get_quantity"
                            type="number"
                            step="1"
                            value={formState.get_quantity}
                            onChange={handleChange}
                            placeholder="e.g., 1"
                            required
                        />
                    </div>
                )}

                <Select
                    label="Applies To"
                    id="applies_to"
                    value={formState.applies_to}
                    onChange={(e) => handleSelectChange('applies_to', e.target.value)}
                    options={Object.values(AppliesToEnum).map(type => ({ value: type, label: type.replace(/_/g, ' ').toUpperCase() }))}
                    required
                />

                {formState.applies_to === AppliesToEnum.PRODUCT && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SearchableSelectBox
                            label="Target Store (for Product/Category)"
                            id="store_id"
                            name="store_id"
                            value={formState.store_id}
                            onChange={(e) => handleSelectChange('store_id', parseInt(e.target.value, 10))}
                            options={storeOptions}
                            placeholder="Select a store"
                            required
                        />
                        <SearchableSelectBox
                            label="Target Product"
                            id="target_product_id"
                            name="target_product_id"
                            value={formState.target_product_id}
                            onChange={(e) => handleSelectChange('target_product_id', parseInt(e.target.value, 10))}
                            options={productOptions}
                            placeholder={loadingInventory ? "Loading products..." : "Select a product"}
                            loading={loadingInventory}
                            disabled={!formState.store_id || loadingInventory} // Disable if no store or loading
                            required
                        />
                    </div>
                )}

                {formState.applies_to === AppliesToEnum.CATEGORY && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SearchableSelectBox
                            label="Target Store (for Product/Category)"
                            id="store_id"
                            name="store_id"
                            value={formState.store_id}
                            onChange={(e) => handleSelectChange('store_id', parseInt(e.target.value, 10))}
                            options={storeOptions}
                            placeholder="Select a store"
                            required
                        />
                        <SearchableSelectBox
                            label="Target Category"
                            id="target_category_id"
                            name="target_category_id"
                            value={formState.target_category_id}
                            onChange={(e) => handleSelectChange('target_category_id', parseInt(e.target.value, 10))}
                            options={categoryOptions}
                            placeholder="Select a category"
                            required
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                        label="Minimum Purchase Amount"
                        id="minimum_purchase_amount"
                        type="number"
                        step="0.01"
                        value={formState.minimum_purchase_amount}
                        onChange={handleChange}
                        placeholder="Optional: e.g., 100.00"
                    />
                    <Input
                        label="Minimum Purchase Quantity"
                        id="minimum_purchase_quantity"
                        type="number"
                        step="1"
                        value={formState.minimum_purchase_quantity}
                        onChange={handleChange}
                        placeholder="Optional: e.g., 5"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                        label="Start Date & Time"
                        id="start_date"
                        type="datetime-local"
                        value={formState.start_date}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="End Date & Time"
                        id="end_date"
                        type="datetime-local"
                        value={formState.end_date}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Checkbox
                        label="Is Active"
                        id="is_active"
                        checked={formState.is_active}
                        onChange={handleChange}
                    />
                    <Checkbox
                        label="Is Store-Wide"
                        id="is_store_wide"
                        checked={formState.is_store_wide}
                        onChange={handleChange}
                        description="Applies to all customers at selected store(s) or globally."
                    />
                </div>

                <Checkbox
                    label="Requires Coupon Code"
                    id="requires_coupon_code"
                    checked={formState.requires_coupon_code}
                    onChange={handleChange}
                />
                {formState.requires_coupon_code && (
                    <Input
                        label="Coupon Code"
                        id="coupon_code"
                        value={formState.coupon_code}
                        onChange={handleChange}
                        placeholder="e.g., SUMMER20"
                        required={formState.requires_coupon_code}
                    />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                        label="Max Uses Per Customer"
                        id="max_uses_per_customer"
                        type="number"
                        step="1"
                        value={formState.max_uses_per_customer}
                        onChange={handleChange}
                        placeholder="Optional: e.g., 1"
                    />
                    <Input
                        label="Total Max Uses"
                        id="total_max_uses"
                        type="number"
                        step="1"
                        value={formState.total_max_uses}
                        onChange={handleChange}
                        placeholder="Optional: e.g., 100"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Select
                        label="Approval Threshold Type"
                        id="approval_threshold_type"
                        value={formState.approval_threshold_type}
                        onChange={(e) => handleSelectChange('approval_threshold_type', e.target.value)}
                        options={Object.values(ApprovalThresholdType).map(type => ({ value: type, label: type.replace(/_/g, ' ').toUpperCase() }))}
                        required
                    />
                    {formState.approval_threshold_type !== ApprovalThresholdType.NONE && (
                        <Input
                            label="Approval Threshold Value"
                            id="approval_threshold_value"
                            type="number"
                            step="0.01"
                            value={formState.approval_threshold_value}
                            onChange={handleChange}
                            placeholder={formState.approval_threshold_type === ApprovalThresholdType.PERCENTAGE_OF_ITEM_VALUE ? "e.g., 10 (for 10%)" : "e.g., 50.00 (for $50)"}
                            required
                        />
                    )}
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                    <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
                        <X size={20} className="mr-2" /> Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : <Save size={20} className="mr-2" />}
                        {initialData ? "Update Discount Rule" : "Create Discount Rule"}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default DiscountRuleForm;
